import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";

// Type-safe icon wrapper
const ChevronDownIcon = FiChevronDown as React.FC<React.SVGProps<SVGSVGElement>>;

interface SidebarDropdownItemProps {
  to: string;
  label: string;
  onClick?: () => void;
}

export const SidebarDropdownItem: React.FC<SidebarDropdownItemProps> = ({
  to,
  label,
  onClick,
}) => {
  const location = useLocation();

  // Check if this item is active by comparing pathname and search params
  const isActive = (): boolean => {
    const [pathname, search] = to.split("?");
    if (location.pathname !== pathname) return false;

    if (search) {
      const targetParams = new URLSearchParams(search);
      const currentParams = new URLSearchParams(location.search);

      // Check if all target params match current params
      let allMatch = true;
      targetParams.forEach((value, key) => {
        if (currentParams.get(key) !== value) allMatch = false;
      });
      return allMatch;
    }

    return true;
  };

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={`su-menu-dropdown-item no-underline ${isActive() ? "su-menu-dropdown-item-active" : ""}`}
    >
      {label}
    </NavLink>
  );
};

interface SidebarDropdownProps {
  icon: React.ReactNode;
  label: string;
  basePath: string;
  children: React.ReactNode;
  onItemClick?: () => void;
}

const SidebarDropdown: React.FC<SidebarDropdownProps> = ({
  icon,
  label,
  basePath,
  children,
  onItemClick,
}) => {
  const location = useLocation();

  // Check if we're currently on a path that starts with basePath
  const isOnBasePath = location.pathname.startsWith(basePath);

  // Initialize open state based on whether we're on a matching route
  const [isOpen, setIsOpen] = useState(isOnBasePath);

  // Auto-open when navigating to a child route
  useEffect(() => {
    if (isOnBasePath && !isOpen) {
      setIsOpen(true);
    }
  }, [isOnBasePath, isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  // Clone children to inject onClick handler
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<SidebarDropdownItemProps>, {
        onClick: onItemClick,
      });
    }
    return child;
  });

  return (
    <div className="su-menu-dropdown">
      <button
        type="button"
        onClick={handleToggle}
        className={`su-menu-dropdown-toggle ${isOnBasePath ? "su-menu-dropdown-toggle-active" : ""}`}
        aria-expanded={isOpen}
      >
        <span className="su-menu-dropdown-label">
          {icon}
          {label}
        </span>
        <ChevronDownIcon
          className={`su-menu-dropdown-chevron ${isOpen ? "su-menu-dropdown-chevron-open" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="su-menu-dropdown-items">
          {childrenWithProps}
        </div>
      )}
    </div>
  );
};

export default SidebarDropdown;
